import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { redirectToCustomerLogin } from '../lib/sso';

// SSO landing point: the customer portal redirects here with the JWT in the
// URL fragment (#sso_token=...). The fragment never reaches any server.
export default function AuthCallback() {
  const navigate = useNavigate();
  const setToken = useAuthStore((s) => s.setToken);
  const ran = useRef(false);
  useEffect(() => {
    // Guard StrictMode double-effect in dev: only run once.
    if (ran.current) return;
    ran.current = true;

    const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const token = params.get('sso_token');
    // Strip the token from the URL/history immediately.
    window.history.replaceState({}, '', window.location.pathname);

    // Loop breaker: the customer portal auto-hands the JWT back whenever it
    // believes it's authenticated. If that token is rejected here (e.g. it was
    // invalidated by a sign-out), bouncing back to login would loop forever.
    // Allow a single round-trip, then land on the public page instead.
    const attempts = Number(sessionStorage.getItem('sso_attempts') || '0');
    const toLanding = () => {
      sessionStorage.removeItem('sso_attempts');
      navigate('/', { replace: true });
    };
    const tryLogin = () => {
      sessionStorage.setItem('sso_attempts', String(attempts + 1));
      redirectToCustomerLogin();
    };

    if (!token) {
      attempts >= 1 ? toLanding() : tryLogin();
      return;
    }
    (async () => {
      try {
        await setToken(token);
        sessionStorage.removeItem('sso_attempts');
        navigate('/console', { replace: true });
      } catch {
        attempts >= 1 ? toLanding() : tryLogin();
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-950">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary-500 border-t-transparent" />
    </div>
  );
}
